import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Shield, Star, ChevronDown, ChevronUp, Loader, Menu, X, Play, Zap, Award, TrendingUp, Heart, Sparkles, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL;

const IconFor = ({ hint, color }) => {
  const cls = 'w-6 h-6';
  const style = color ? { color } : {};
  switch ((hint || '').toLowerCase()) {
    case 'zap': return <Zap className={cls} style={style} />;
    case 'shield': return <Shield className={cls} style={style} />;
    case 'star': return <Star className={cls} style={style} />;
    case 'heart': return <Heart className={cls} style={style} />;
    case 'award': return <Award className={cls} style={style} />;
    case 'trending-up': case 'trending_up': return <TrendingUp className={cls} style={style} />;
    default: return <Check className={cls} style={style} />;
  }
};

const mediaUrl = (u) => {
  if (!u) return null;
  if (u.startsWith('http')) return u;
  return `${MEDIA_BASE}${u}`;
};

const isVideoUrl = (u) => {
  if (!u) return false;
  return /\.(mp4|mov|webm)(\?|$)/i.test(u);
};

export const PublicSalesPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API_URL}/p/${slug}`);
        setPage(response.data);
      } catch (err) {
        setError('Página não encontrada');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Página não encontrada</h1>
          <p className="text-gray-600">O link pode estar incorreto ou a página foi removida.</p>
        </div>
      </div>
    );
  }

  const { content, colors, form_data } = page;
  const primaryColor = colors?.primary || '#7c3aed';
  const secondaryColor = colors?.secondary || '#a78bfa';
  const accentColor = colors?.accent || '#f3e8ff';
  const neutralDark = colors?.neutral_dark || '#111827';
  const neutralLight = colors?.neutral_light || '#f9fafb';

  const isPremium = !!(page.premium_mode && content?.site_meta);
  const meta = content?.site_meta || {};
  const logoUrl = mediaUrl(content?.logo_url);
  const hero = content?.hero || {};
  const about = content?.about_section || {};
  const productSec = content?.product_section || {};
  const gallerySec = content?.gallery_section || {};
  const videoSec = content?.video_section || {};
  const authoritySec = content?.authority_section || {};
  const finalCta = content?.final_cta || {};
  const footer = content?.footer || {};

  const menuItems = meta.menu_items || [
    { label: 'Início', anchor: 'hero' },
    { label: 'Produto', anchor: 'product' },
    { label: 'Benefícios', anchor: 'benefits' },
    { label: 'Depoimentos', anchor: 'testimonials' },
    { label: 'FAQ', anchor: 'faq' },
  ];

  const heroBg = mediaUrl(hero.background_image_url);
  const heroVideo = mediaUrl(hero.background_video_url);
  const heroBadges = hero.badges || content?.hero_badges || [];

  // ============ NON-PREMIUM (legacy) fallback rendering ============
  if (!isPremium) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#ffffff' }}>
        <section className="py-20 px-6" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">{content?.headline || page.title}</h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">{content?.subheadline}</p>
            <p className="text-lg mb-10 opacity-80 max-w-2xl mx-auto">{content?.hero_description}</p>
            {form_data?.price && (
              <div className="mb-8">
                {form_data?.original_price && form_data.original_price > form_data.price && (
                  <p className="text-lg line-through opacity-60 mb-1">De R$ {form_data.original_price.toFixed(2)}</p>
                )}
                <p className="text-4xl font-bold">R$ {form_data.price.toFixed(2)}</p>
              </div>
            )}
            <a href={form_data?.cta_link || '#'} className="inline-block px-8 py-4 text-lg font-bold rounded-xl shadow-lg" style={{ backgroundColor: '#ffffff', color: primaryColor }}>{form_data?.cta_text || 'Comprar Agora'}</a>
          </div>
        </section>
        {content?.benefits?.length > 0 && (
          <section className="py-20 px-6" style={{ backgroundColor: accentColor }}>
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12" style={{ color: primaryColor }}>Por que escolher?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {content.benefits.map((b, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: accentColor }}>
                      <Check className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900">{b.title}</h3>
                    <p className="text-gray-600">{b.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        <footer className="py-8 px-6 bg-gray-900 text-center text-gray-400">
          <p className="text-sm">© {new Date().getFullYear()} - Todos os direitos reservados</p>
        </footer>
      </div>
    );
  }

  // ============ PREMIUM MINI-SITE RENDERING ============
  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ '--brand-primary': primaryColor, '--brand-secondary': secondaryColor, '--brand-accent': accentColor }}>
      {/* HEADER */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/95 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={meta.brand_name || 'Logo'} className="h-10 md:h-12 w-auto object-contain" />
            ) : (
              <span className="font-bold text-xl" style={{ color: primaryColor }}>{meta.brand_name || page.title}</span>
            )}
          </a>
          <nav className="hidden md:flex items-center gap-7">
            {menuItems.map((m, i) => (
              <a key={i} href={`#${m.anchor}`} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">{m.label}</a>
            ))}
            <a href={form_data?.cta_link || '#final-cta'} className="px-4 py-2 rounded-full text-sm font-semibold text-white shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: primaryColor }}>
              {form_data?.cta_text || 'Comprar'}
            </a>
          </nav>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-md hover:bg-gray-100">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <nav className="flex flex-col gap-1 p-4">
              {menuItems.map((m, i) => (
                <a key={i} href={`#${m.anchor}`} onClick={() => setMobileMenuOpen(false)} className="px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50">{m.label}</a>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="hero" className="relative overflow-hidden">
        {heroVideo && (
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src={heroVideo} />
          </video>
        )}
        {!heroVideo && heroBg && (
          <div className="absolute inset-0">
            <img src={heroBg} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="absolute inset-0" style={{ background: heroBg || heroVideo ? `linear-gradient(135deg, ${primaryColor}dd 0%, ${secondaryColor}bb 100%)` : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 md:py-32 text-white">
          {heroBadges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              {heroBadges.map((b, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm border border-white/30">{b}</span>
              ))}
            </div>
          )}
          <h1 className="text-4xl md:text-6xl font-bold text-center leading-tight tracking-tight mb-6 max-w-4xl mx-auto">{hero.headline || content?.headline || page.title}</h1>
          <p className="text-lg md:text-2xl text-center opacity-95 mb-4 max-w-3xl mx-auto">{hero.subheadline || content?.subheadline}</p>
          {(hero.description || content?.hero_description) && (
            <p className="text-base md:text-lg text-center opacity-85 mb-10 max-w-2xl mx-auto">{hero.description || content?.hero_description}</p>
          )}
          {form_data?.price && (
            <div className="flex flex-col items-center gap-1 mb-8">
              {form_data?.original_price > form_data.price && (
                <p className="text-lg line-through opacity-70">De R$ {form_data.original_price.toFixed(2)}</p>
              )}
              <p className="text-5xl font-bold">R$ {form_data.price.toFixed(2)}</p>
              <p className="text-sm opacity-80">ou em até 12x sem juros</p>
            </div>
          )}
          <div className="flex flex-col items-center gap-3">
            <a href={form_data?.cta_link || '#final-cta'} className="inline-block px-10 py-4 text-lg font-bold rounded-full shadow-2xl hover:scale-105 transition-transform" style={{ backgroundColor: '#ffffff', color: primaryColor }}>
              {form_data?.cta_text || 'Comprar Agora'}
            </a>
            {content?.urgency_text && (
              <p className="text-xs opacity-90 italic">⏳ {content.urgency_text}</p>
            )}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      {(about.text || about.image_url) && (
        <section id="about" className="py-20 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            {mediaUrl(about.image_url) && (
              <div className="order-2 md:order-1">
                <img src={mediaUrl(about.image_url)} alt="Sobre a marca" className="w-full rounded-2xl shadow-xl aspect-[4/3] object-cover" />
              </div>
            )}
            <div className={`order-1 md:order-${mediaUrl(about.image_url) ? '2' : '1'}`}>
              <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: primaryColor }}>Sobre nós</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: neutralDark }}>{about.title || 'Nossa história'}</h2>
              <p className="text-lg text-gray-700 leading-relaxed">{about.text}</p>
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT */}
      {(productSec.main_image_url || productSec.description) && (
        <section id="product" className="py-20 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: primaryColor }}>O produto</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: neutralDark }}>{productSec.title || 'Conheça o produto'}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">{productSec.description}</p>
              <a href={form_data?.cta_link || '#final-cta'} className="inline-block px-6 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105" style={{ backgroundColor: primaryColor }}>
                {form_data?.cta_text || 'Quero conhecer'}
              </a>
            </div>
            {mediaUrl(productSec.main_image_url) && (
              <div>
                <img src={mediaUrl(productSec.main_image_url)} alt="Produto" className="w-full rounded-2xl shadow-xl aspect-square object-cover" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* VIDEO */}
      {mediaUrl(videoSec.video_url) && (
        <section className="py-20 px-6" style={{ backgroundColor: neutralDark }}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{videoSec.title || 'Veja em ação'}</h2>
            {videoSec.description && <p className="text-lg opacity-80 mb-8">{videoSec.description}</p>}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black">
              <video src={mediaUrl(videoSec.video_url)} controls playsInline className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {content?.benefits?.length > 0 && (
        <section id="benefits" className="py-20 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: primaryColor }}>Benefícios</p>
              <h2 className="text-3xl md:text-4xl font-bold" style={{ color: neutralDark }}>Por que escolher</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.benefits.map((benefit, i) => (
                <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: accentColor }}>
                    <IconFor hint={benefit.icon_hint} color={primaryColor} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {gallerySec.show && gallerySec.image_urls?.length > 0 && (
        <section id="gallery" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ color: neutralDark }}>{gallerySec.title || 'Galeria'}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallerySec.image_urls.map((u, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group cursor-pointer" onClick={() => setGalleryIndex(i)}>
                  <img src={mediaUrl(u)} alt={`Galeria ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DIFFERENTIATORS */}
      {content?.differentiators?.length > 0 && (
        <section className="py-20 px-6" style={{ backgroundColor: accentColor }}>
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: primaryColor }}>Diferenciais</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-14" style={{ color: neutralDark }}>O que nos torna únicos</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {content.differentiators.map((d, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm text-left">
                  <div className="text-4xl font-bold mb-3" style={{ color: primaryColor }}>0{i + 1}</div>
                  <h3 className="font-semibold text-lg mb-2">{d.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{d.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AUTHORITY */}
      {authoritySec.items?.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: neutralDark }}>{authoritySec.title}</h2>
              <ul className="space-y-3">
                {authoritySec.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: primaryColor }} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {mediaUrl(authoritySec.image_url) && (
              <img src={mediaUrl(authoritySec.image_url)} alt="Autoridade" className="w-full rounded-2xl shadow-xl aspect-[4/3] object-cover" />
            )}
          </div>
        </section>
      )}

      {/* FEATURES */}
      {content?.features?.length > 0 && (
        <section className="py-20 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: neutralDark }}>O que você recebe</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: primaryColor }} />
                  <span className="text-gray-800">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {form_data?.include_testimonials && content?.testimonials?.length > 0 && (
        <section id="testimonials" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: primaryColor }}>Depoimentos</p>
              <h2 className="text-3xl md:text-4xl font-bold" style={{ color: neutralDark }}>O que dizem nossos clientes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating || 5)].map((_, k) => (
                      <Star key={k} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic leading-relaxed text-sm">"{t.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OBJECTIONS */}
      {content?.objections?.length > 0 && (
        <section className="py-20 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: neutralDark }}>Suas dúvidas, nossas respostas</h2>
            <div className="space-y-4">
              {content.objections.map((o, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border-l-4 shadow-sm" style={{ borderColor: primaryColor }}>
                  <p className="font-semibold mb-2 text-gray-900">"{o.objection}"</p>
                  <p className="text-gray-700 leading-relaxed">{o.response}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GUARANTEE */}
      {form_data?.include_guarantee && (
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ backgroundColor: accentColor }}>
              <Shield className="w-12 h-12" style={{ color: primaryColor }} />
            </div>
            {content?.guarantee_badge && (
              <div className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: accentColor, color: primaryColor }}>
                {content.guarantee_badge}
              </div>
            )}
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: neutralDark }}>Garantia de {form_data.guarantee_days} dias</h2>
            <p className="text-lg text-gray-700 leading-relaxed">{content?.guarantee_text}</p>
          </div>
        </section>
      )}

      {/* FAQ */}
      {form_data?.include_faq && content?.faq?.length > 0 && (
        <section id="faq" className="py-20 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: neutralDark }}>Perguntas Frequentes</h2>
            <div className="space-y-3">
              {content.faq.map((item, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 flex-shrink-0 text-gray-500" /> : <ChevronDown className="w-5 h-5 flex-shrink-0 text-gray-500" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 pt-0 text-gray-700 leading-relaxed">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section id="final-cta" className="relative py-24 px-6 overflow-hidden">
        {mediaUrl(finalCta.background_image_url) && (
          <img src={mediaUrl(finalCta.background_image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}f0 0%, ${secondaryColor}e0 100%)` }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">{finalCta.headline || content?.final_cta_headline || 'Não perca essa oportunidade!'}</h2>
          {finalCta.description && <p className="text-lg opacity-90 mb-8">{finalCta.description}</p>}
          {form_data?.price && (
            <p className="text-5xl font-bold mb-8">R$ {form_data.price.toFixed(2)}</p>
          )}
          <a href={form_data?.cta_link || '#'} className="inline-block px-10 py-5 text-xl font-bold rounded-full shadow-2xl hover:scale-105 transition-transform" style={{ backgroundColor: '#ffffff', color: primaryColor }}>
            {form_data?.cta_text || 'Comprar Agora'}
          </a>
          {content?.scarcity_text && (
            <p className="mt-6 text-sm opacity-90">🔥 {content.scarcity_text}</p>
          )}
        </div>
      </section>

      {/* CONTACT */}
      {content?.contact_section?.show !== false && (
        <section id="contact" className="py-16 px-6" style={{ backgroundColor: neutralLight }}>
          <div className="max-w-3xl mx-auto text-center">
            <MessageCircle className="w-10 h-10 mx-auto mb-4" style={{ color: primaryColor }} />
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: neutralDark }}>{content?.contact_section?.title || 'Fale conosco'}</h2>
            <p className="text-gray-700">{content?.contact_section?.text || 'Estamos prontos para atender você.'}</p>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="py-10 px-6" style={{ backgroundColor: neutralDark }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {mediaUrl(footer.logo_url) || logoUrl ? (
              <img src={mediaUrl(footer.logo_url) || logoUrl} alt="Logo" className="h-8 w-auto object-contain brightness-0 invert opacity-90" />
            ) : (
              <span className="text-white font-bold">{meta.brand_name || page.title}</span>
            )}
          </div>
          <p className="text-sm text-gray-400">{footer.text || meta.tagline || content?.tagline || `© ${new Date().getFullYear()} — Todos os direitos reservados`}</p>
          <p className="text-xs text-gray-500">Criado com Needi</p>
        </div>
      </footer>
    </div>
  );
};
